import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from '@mui/material';
import PropTypes from 'prop-types';
import closeIcon from '../../../../../assets/accountSetting-assets/x-circle.svg';
import { Root, classes } from './utils';

import Visibility from '../../../../../assets/common-assets/eye.svg';
import VisibilityOff from '../../../../../assets/common-assets/eye-off.svg';
import {
  CHANGEPASSWORD,
  ERROR,
  INVALID_INPUT_MESSAGES,
  INVALID_INPUT_REGEX,
} from '../../../../../constants/staticContent';
import { changePassword } from '../../../../../services/passwordApi';

const ChangePassword = ({ setOpen }) => {
  const navigate = useNavigate();
  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [formData, setFormData] = useState({
    oldPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [errors, setErrors] = useState({
    oldPassword: false,
    newPassword: false,
    confirmPassword: false,
  });

  const closeChangePassword = () => {
    setOpen(false);
    setFormData({
      oldPassword: '',
      newPassword: '',
      confirmPassword: '',
    });
    setErrors({});
  };

  const handleClickShowOldPassword = () => setShowOldPassword((show) => !show);
  const handleClickShowNewPassword = () => setShowNewPassword((show) => !show);
  const handleClickShowConfirmPassword = () =>
    setShowConfirmPassword((show) => !show);

  const validatePassword = (password) => {
    const passwordRegex = INVALID_INPUT_REGEX.PASSWORD;
    return passwordRegex.test(password);
  };
  const validateEmptyField = (password) => password !== '';
  const newConfirmSimilarity = (newPass, confirmPass) =>
    newPass === confirmPass;

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevFormData) => ({
      ...prevFormData,
      [name]: value,
    }));
    if (name === 'newPassword') {
      const isValid = validatePassword(value);
      setErrors((prevErrors) => ({
        ...prevErrors,
        newPassword: !isValid,
      }));
    } else if (name === 'confirmPassword') {
      const isValid = value === formData.newPassword;
      setErrors((prevErrors) => ({
        ...prevErrors,
        confirmPassword: !isValid,
      }));
    } else if (name === 'oldPassword') {
      setErrors((prevErrors) => ({
        ...prevErrors,
        oldPassword: value.trim() === '',
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const { oldPassword } = formData;
    const { newPassword } = formData;
    const confirmNewPassword = formData.confirmPassword;
    const newPasswordValid = validatePassword(newPassword);
    const confirmPasswordValid = validateEmptyField(confirmNewPassword);
    const oldPasswordValid = validateEmptyField(oldPassword);
    const similarityValid = newConfirmSimilarity(
      newPassword,
      confirmNewPassword,
    );

    setErrors({
      newPassword: !newPasswordValid,
      confirmPassword: !confirmPasswordValid || !similarityValid,
      oldPassword: !oldPasswordValid,
    });

    if (
      newPasswordValid &&
      confirmPasswordValid &&
      oldPasswordValid &&
      similarityValid
    ) {
      const response = await changePassword(
        oldPassword,
        newPassword,
        confirmNewPassword,
      );
      if (response) {
        navigate('/login');
      }
    }
  };

  const hasAnyError = Object.values(errors).some((value) => value === true);
  const getHelperText = (fieldName) => {
    const fieldError = errors[fieldName];
    const fieldValue = formData[fieldName].trim();

    if (fieldError) {
      if (fieldValue === '') {
        return ERROR.EMPTY_ERROR;
      }
      switch (fieldName) {
        case 'newPassword':
          return INVALID_INPUT_MESSAGES.PASSWORD;
        case 'confirmPassword':
          return INVALID_INPUT_MESSAGES.CONFIRM_PASSWORD;
        default:
          break;
      }
    }

    return '';
  };

  return (
    <Root className={classes.root}>
      <Card
        className={
          hasAnyError ? classes.errorCardComponent : classes.CardComponent
        }
      >
        <CardHeader
          className={classes.CardHeader}
          title={
            <Grid container alignItems="center">
              <Grid item xs={10}>
                <Typography variant="h6" className={classes.HeaderTypography}>
                  {CHANGEPASSWORD.CHANGE_PASSWORD}
                </Typography>
              </Grid>
              <Grid item xs={2} align="right">
                <Avatar
                  onClick={closeChangePassword}
                  src={closeIcon}
                  className={classes.CloseAction}
                />
              </Grid>
            </Grid>
          }
        />
        <CardContent className={classes.CardContent}>
          <Box component="form" noValidate onSubmit={handleSubmit}>
            <FormControl fullWidth>
              <Box className={classes.CardBox}>
                <Grid container>
                  <Grid item xs={4}>
                    <Typography
                      variant="subtitle1"
                      className={classes.GridTypography}
                    >
                      {CHANGEPASSWORD.OLD_PASSWORD}
                    </Typography>
                  </Grid>
                  <Grid item xs={8}>
                    <TextField
                      size="small"
                      fullWidth
                      className={classes.GridTextfield}
                      id="oldPassword"
                      name="oldPassword"
                      autoComplete="oldPassword"
                      onChange={handleInputChange}
                      value={formData.oldPassword}
                      error={errors.oldPassword}
                      helperText={getHelperText('oldPassword')}
                      autoFocus
                      type={showOldPassword ? 'text' : 'password'}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              aria-label="toggle password visibility"
                              onClick={handleClickShowOldPassword}
                              edge="end"
                            >
                              {showOldPassword ? (
                                <Box
                                  component="img"
                                  className={classes.EyeIconForPassord}
                                  src={Visibility}
                                  alt="EyeOnIcon"
                                />
                              ) : (
                                <Box
                                  component="img"
                                  className={classes.EyeIconForPassord}
                                  src={VisibilityOff}
                                  alt="EyeOffIcon"
                                />
                              )}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                </Grid>
              </Box>

              <Box className={classes.CardBox}>
                <Grid container>
                  <Grid item xs={4}>
                    <Typography
                      variant="subtitle1"
                      className={classes.GridTypography}
                    >
                      {CHANGEPASSWORD.NEW_PASSWORD}
                    </Typography>
                  </Grid>
                  <Grid item xs={8}>
                    <TextField
                      size="small"
                      fullWidth
                      className={classes.GridTextfield}
                      id="newPassword"
                      name="newPassword"
                      autoComplete="newPassword"
                      onChange={handleInputChange}
                      value={formData.newPassword}
                      error={errors.newPassword}
                      helperText={getHelperText('newPassword')}
                      type={showNewPassword ? 'text' : 'password'}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              aria-label="toggle password visibility"
                              onClick={handleClickShowNewPassword}
                              edge="end"
                            >
                              {showNewPassword ? (
                                <Box
                                  component="img"
                                  className={classes.EyeIconForPassord}
                                  src={Visibility}
                                  alt="EyeOnIcon"
                                />
                              ) : (
                                <Box
                                  component="img"
                                  className={classes.EyeIconForPassord}
                                  src={VisibilityOff}
                                  alt="EyeOffIcon"
                                />
                              )}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                </Grid>
              </Box>

              <Box className={classes.CardBox}>
                <Grid container>
                  <Grid item xs={4}>
                    <Typography
                      variant="subtitle1"
                      className={classes.GridTypography}
                    >
                      {CHANGEPASSWORD.CONFIRM_NEW_PASSWORD}
                    </Typography>
                  </Grid>
                  <Grid item xs={8}>
                    <TextField
                      size="small"
                      fullWidth
                      className={classes.GridTextfield}
                      id="confirm_new_password"
                      name="confirmPassword"
                      autoComplete="confirm_new_password"
                      onChange={handleInputChange}
                      value={formData.confirmPassword}
                      error={errors.confirmPassword}
                      helperText={getHelperText('confirmPassword')}
                      // helperText={
                      //   errors.confirm_password
                      //     ? formData.confirm_password.trim() === ''
                      //       ? 'Please fill out this field'
                      //       : 'Passwords do not match'
                      //     : ''
                      // }
                      type={showConfirmPassword ? 'text' : 'password'}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              aria-label="toggle password visibility"
                              onClick={handleClickShowConfirmPassword}
                              edge="end"
                            >
                              {showConfirmPassword ? (
                                <Box
                                  component="img"
                                  className={classes.EyeIconForPassord}
                                  src={Visibility}
                                  alt="EyeOnIcon"
                                />
                              ) : (
                                <Box
                                  component="img"
                                  className={classes.EyeIconForPassord}
                                  src={VisibilityOff}
                                  alt="EyeOffIcon"
                                />
                              )}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                </Grid>
              </Box>
              <Box className={classes.GridBoxButton}>
                {/* <Grid container justifyContent={'flex-end'}>
                  <Grid item xs={3}> */}
                <Button
                  type="submit"
                  variant="contained"
                  className={classes.GridButton}
                >
                  {CHANGEPASSWORD.CHANGE_PASSWORD}
                </Button>
                {/* </Grid>
                </Grid> */}
              </Box>
            </FormControl>
          </Box>
        </CardContent>
      </Card>
    </Root>
  );
};

ChangePassword.propTypes = {
  setOpen: PropTypes.func.isRequired,
};

export default ChangePassword;
